// App.jsx
import React, { useState, useEffect } from 'react';
import HomePage from '../pages/Homepage';
import { Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Home, Facebook, Twitter, Instagram, Youtube, Linkedin, ChevronDown } from 'lucide-react';

// Scroll to Top Component
const ScrollToTop = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate(); 

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [pathname]);

  return null;
};

// Header Component
const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showSolutionsDropdown, setShowSolutionsDropdown] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Handle navigation with scroll to top
  const handleNavigation = (path) => {
    navigate(path);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    setIsMenuOpen(false);
    setShowSolutionsDropdown(false);
  };
  const handleButtonClick = () => {
    navigate('/product'); // Navigate to the /products page
  };
  const navigation = [
    { name: 'Home', path: '/', icon: Home },
    { name: 'About', path: '/about' },
    {
      name: 'Solutions',
      path: '/solution',
      hasDropdown: true,
      dropdownItems: [
        {
          category: 'Cloud and Telecom',
          path: '/solution/cloud-telecom'
        },
        {
          category: 'Application and Data Management',
          path: '/solution/app-data-management'
        }
      ]
    },
    { name: 'Services', path: '/services' },
    { name: 'Products', path: '/product' },
    { name: 'Partners', path: '/partners' },
    { name: 'Contact Us', path: '/contactus' }
  ];

  const socialIcons = [
    { Icon: Facebook, path: '#facebook', label: 'Facebook' },
    { Icon: Twitter, path: '#twitter', label: 'Twitter' },
    { Icon: Instagram, path: '#instagram', label: 'Instagram' },
    { Icon: Youtube, path: '#youtube', label: 'Youtube' },
    { Icon: Linkedin, path: '#linkedin', label: 'LinkedIn' }
  ];

  const handleMouseEnter = (item) => {
    if (item.hasDropdown) {
      setShowSolutionsDropdown(true);
    }
  };

  const handleMouseLeave = () => {
    setShowSolutionsDropdown(false);
  };

  const handleSolutionClick = (item) => {
    if (item.hasDropdown) {
      handleNavigation(item.path);
      setShowSolutionsDropdown(!showSolutionsDropdown);
    } else {
      handleNavigation(item.path);
    }
  };

  const commonLinkStyles = {
    color: '#410684',
    textDecoration: 'none',
    fontWeight: 'bold',
    fontFamily: 'Raleway, sans-serif',
    fontSize: isMobile ? '14px' : '16px',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  };

  return (
    <>
      {/* Top Header - Not Sticky */}
      <div style={{
        backgroundColor: '#410684',
        padding: isMobile ? '8px 16px' : '0px 32px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: isMobile ? '15px' : '30px'
        }}>
          {socialIcons.map(({ Icon, path, label }) => (
            <a 
              key={label}
              href={path}
              aria-label={label}
              style={{
                color: '#9596AA',
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                transition: 'color 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#ffffff'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#9596AA'}
            >
              <Icon size={isMobile ? 16 : 20} />
            </a>
          ))}
        </div>

        <button style={{
          backgroundColor: '#DC2666',
          color: 'white',
          padding: isMobile ? '15px 17px' : '16px 20px',
          border: 'none',
          fontWeight: 'bold',
          fontFamily: 'Raleway, sans-serif',
          fontSize: isMobile ? '14px' : '16px',
          cursor: 'pointer',
          transition: 'background-color 0.2s ease'
        }}
        onClick={handleButtonClick}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#DC2565'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#DC2666'}
        >
          Explore Our Products
        </button>
      </div>

      {/* Main Header - Sticky */}
      <div style={{
        position: 'sticky',
        top: 0,
        backgroundColor: 'white',
        zIndex: 1000,
      }}>
        <div style={{
          padding: '0px 32px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          height: isMobile ? '70px' : '90px',
          width: '100%'
        }}>
          {/* Logo */}
          <div style={{
            display: 'flex',
            alignItems: 'center'
          }}>
            <img 
              src="/LLC.png"
              alt="Logo"
              style={{
                marginLeft: '20px',
                maxHeight: isMobile ? '40px' : '60px',
                objectFit: 'contain'
              }}
            />
          </div>

          {/* Mobile Menu Toggle */}
          {isMobile && (
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '8px',
                color: '#410684'
              }}
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          )}

          {/* Desktop Navigation */}
          {!isMobile && (
            <nav style={{
              display: 'flex',
              alignItems: 'center',
              gap: '24px'
            }}>
              {navigation.map((item) => (
                <div
                  key={item.name}
                  onMouseEnter={() => handleMouseEnter(item)}
                  onMouseLeave={handleMouseLeave}
                  style={{ position: 'relative' }}
                >
                  <a
                    onClick={() => handleSolutionClick(item)}
                    style={{
                      ...commonLinkStyles,
                      padding: '8px',
                      color: location.pathname === item.path ? '#DC2565' : '#410684',
                      borderBottom: location.pathname === item.path ? '2px solid #DC2565' : 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    {item.name === 'Home' ? <Home size={20} /> : item.name}
                    {item.hasDropdown && <ChevronDown size={16} />}
                  </a>
                  {item.hasDropdown && showSolutionsDropdown && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      backgroundColor: 'white',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                      borderRadius: '4px',
                      minWidth: '250px',
                      zIndex: 1000
                    }}>
                      {item.dropdownItems.map((dropdownItem) => (
                        <a
                          key={dropdownItem.category}
                          onClick={() => handleNavigation(dropdownItem.path)}
                          style={{
                            ...commonLinkStyles,
                            display: 'block',
                            padding: '12px 16px',
                            color: '#410684',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          {dropdownItem.category}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>
          )}

          {/* Mobile Navigation Menu */}
          {isMobile && isMenuOpen && (
            <div style={{
              position: 'absolute',
              top: '70px',
              left: 0,
              right: 0,
              backgroundColor: 'white',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              padding: '16px',
              zIndex: 1000
            }}>
              <nav style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px'
              }}>
                {navigation.map((item) => (
                  <div key={item.name}>
                    <a
                      onClick={() => handleSolutionClick(item)}
                      style={{
                        ...commonLinkStyles,
                        padding: '8px',
                        color: location.pathname === item.path ? '#DC2565' : '#410684',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '8px'
                      }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {item.name === 'Home' && <Home size={20} />}
                        {item.name}
                      </span>
                      {item.hasDropdown && <ChevronDown size={16} />}
                    </a>
                    {item.hasDropdown && showSolutionsDropdown && (
                      <div style={{
                        marginLeft: '16px',
                        marginTop: '8px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px'
                      }}>
                        {item.dropdownItems.map((dropdownItem) => (
                          <a
                            key={dropdownItem.category}
                            onClick={() => handleNavigation(dropdownItem.path)}
                            style={{
                              ...commonLinkStyles,
                              padding: '8px'
                            }}
                          >
                            {dropdownItem.category}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </nav>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// App Component
// const App = () => {
//   return (
//     <>
//       <ScrollToTop />
//       <Header />
//       <Routes>
//         <Route path="/" element={<HomePage />} />
//         {/* Add other routes here */}
//       </Routes>
//     </>
//   );
// };

export default Header;